import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';

import type { StatusTone } from '@/components/ui/status-badge';
import { CONFIDENTIALITY_LEVELS, DISPLAY_TARGETS, DOCUMENT_CATEGORIES } from '@/types/document';

type CategoryValue = (typeof DOCUMENT_CATEGORIES)[number]['value'];
type ConfidentialityValue = (typeof CONFIDENTIALITY_LEVELS)[number]['value'];
type DisplayTargetValue = (typeof DISPLAY_TARGETS)[number]['value'];

/** Translated names for the document category values (the type constants carry the values only). */
export const DOCUMENT_CATEGORY_LABELS: Record<CategoryValue, MessageDescriptor> = {
  agb: msg({ id: 'documents.category.agb', message: 'Terms and conditions' }),
  staff_guidelines: msg({ id: 'documents.category.staffGuidelines', message: 'Staff guidelines' }),
  instructions: msg({ id: 'documents.category.instructions', message: 'Instructions' }),
  policies: msg({ id: 'documents.category.policies', message: 'Policies' }),
  info_sheets: msg({ id: 'documents.category.infoSheets', message: 'Info sheets' }),
  other: msg({ id: 'documents.category.other', message: 'Other' }),
};

export const CONFIDENTIALITY_LABELS: Record<ConfidentialityValue, MessageDescriptor> = {
  public: msg({ id: 'documents.confidentiality.public', message: 'Public' }),
  internal: msg({ id: 'documents.confidentiality.internal', message: 'Internal' }),
  confidential: msg({ id: 'documents.confidentiality.confidential', message: 'Confidential' }),
};

export const DISPLAY_TARGET_LABELS: Record<DisplayTargetValue, MessageDescriptor> = {
  parent_app: msg({ id: 'documents.displayTarget.parentApp', message: 'Parent app' }),
  employee_app: msg({ id: 'documents.displayTarget.employeeApp', message: 'Employee app' }),
};

/** Who may read it, as a tone: confidential stands out, public stays quiet. */
export const CONFIDENTIALITY_TONES: Record<ConfidentialityValue, StatusTone> = {
  public: 'neutral',
  internal: 'info',
  confidential: 'danger',
};

export function isCategory(value: string | undefined): value is CategoryValue {
  return !!value && value in DOCUMENT_CATEGORY_LABELS;
}

export function isConfidentiality(value: string | undefined): value is ConfidentialityValue {
  return !!value && value in CONFIDENTIALITY_LABELS;
}
